import { computed, onMounted, ref, watch } from "vue";
import { useStore } from 'vuex';
import { MAX_IDS_POR_SOLICITUD } from '../../store/propertyStore';

// Eliminar o trasladar los inmuebles de un agente (con o sin sus agentes
// hijos), por seleccion de ids o todos. Contrato en el backend:
// docs/inmuebles-por-agente-contrato-api.md
export function useEliminacionMasiva() {
    const store = useStore();

    const spinner = ref(false);
    const confirmActive = ref(false);
    const alertTable = ref({ state: false, message: '', color: 'success' });
    const resultado = ref(null);

    let alertTimer = null;
    const showAlert = (message, color = 'success') => {
        if (alertTimer) clearTimeout(alertTimer);
        alertTable.value = { state: true, message, color };
        alertTimer = setTimeout(() => { alertTable.value.state = false; }, 6000);
    };

    const mensajeError = (error, porDefecto) => {
        const payload = error.response?.data;
        if (Array.isArray(payload?.errorMessage) && payload.errorMessage.length) {
            return payload.errorMessage.join(' · ');
        }
        return typeof payload?.data === 'string' ? payload.data : porDefecto;
    };

    // ── 1. Selector de agente ────────────────────────────────────────────────
    const agentes = ref([]);
    const agenteSel = ref(null);
    const loadingAgentes = ref(false);
    // El super admin no recibe lista inicial: debe buscar (3+ letras).
    const requiereBusqueda = ref(false);

    const conEtiqueta = (a) => ({ ...a, label: `${a.nombre} — ${a.email}` });

    const cargarAgentes = async (search = '') => {
        loadingAgentes.value = true;
        try {
            const data = await store.dispatch('appProperties/agentesPorAgenteAction', search);
            agentes.value = data.map(conEtiqueta);
        } catch (error) {
            if (error.response?.status === 422) {
                requiereBusqueda.value = true;
                agentes.value = [];
            } else {
                showAlert(mensajeError(error, 'No se pudo cargar la lista de agentes'), 'danger');
            }
        } finally {
            loadingAgentes.value = false;
        }
    };

    let searchTimer = null;
    const buscarAgentes = (termino) => {
        if (searchTimer) clearTimeout(searchTimer);
        const t = (termino || '').trim();
        if (requiereBusqueda.value && t.length < 3) return;
        searchTimer = setTimeout(() => cargarAgentes(t), 350);
    };

    const textoSinAgentes = (search) => {
        if (loadingAgentes.value) return 'Buscando…';
        if (requiereBusqueda.value && (search || '').trim().length < 3) {
            return 'Escribe al menos 3 letras del correo o nombre';
        }
        return 'No hay agentes con ese criterio';
    };

    // ── 2. Resumen del agente ────────────────────────────────────────────────
    const resumen = ref(null);
    const loadingResumen = ref(false);
    const incluirHijos = ref(false);

    const cargarResumen = async () => {
        if (!agenteSel.value) return;
        loadingResumen.value = true;
        try {
            resumen.value = await store.dispatch('appProperties/resumenPorAgenteAction', agenteSel.value.email);
        } catch (error) {
            resumen.value = null;
            showAlert(mensajeError(error, 'No se pudo cargar el resumen del agente'), 'danger');
        } finally {
            loadingResumen.value = false;
        }
    };

    const tieneHijos = computed(() => (resumen.value?.agente?.hijos_count || 0) > 0);
    const totalAlcance = computed(() => {
        const t = resumen.value?.totales;
        if (!t) return 0;
        return incluirHijos.value ? t.incluye_hijos : t.propios;
    });

    // ── 3. Accion, alcance y destino ─────────────────────────────────────────
    const accion = ref('eliminar');   // eliminar | trasladar
    const alcance = ref('all');       // all | ids
    const destinoSel = ref(null);

    const destinos = computed(() =>
        (resumen.value?.destinos || [])
            .filter((d) => d.id !== agenteSel.value?.id)
            .map((d) => ({ ...d, label: `${d.nombre} — ${d.email}${d.es_admin ? ' (admin)' : ''}` }))
    );

    watch(agenteSel, () => {
        resumen.value = null;
        resultado.value = null;
        incluirHijos.value = false;
        destinoSel.value = null;
        cargarResumen();
    });

    // ── Ids pegados (alcance = ids) ──────────────────────────────────────────
    const entrada = ref('');

    // El usuario pega los ids como quiera: comas, espacios, saltos de linea o
    // punto y coma. Separamos por cualquier cosa que no sea un digito.
    const tokens = computed(() =>
        entrada.value.split(/[^0-9]+/).filter((t) => t !== '')
    );

    // Lo que el usuario escribio pero no es un id valido (letras, decimales,
    // negativos): se muestra como aviso, no bloquea el envio.
    const invalidos = computed(() => {
        const sueltos = entrada.value.split(/[\s,;]+/).filter((t) => t !== '');
        return sueltos.filter((t) => !/^\d+$/.test(t) || Number(t) < 1);
    });

    const ids = computed(() => {
        const vistos = new Set();
        const limpios = [];
        tokens.value.forEach((t) => {
            const n = Number(t);
            if (n >= 1 && !vistos.has(n)) {
                vistos.add(n);
                limpios.push(n);
            }
        });
        return limpios;
    });

    const duplicados = computed(() => tokens.value.length - ids.value.length);
    const excedeLimite = computed(() => ids.value.length > MAX_IDS_POR_SOLICITUD);

    // Lo que se va a procesar: con ids es un maximo (el backend ignora los que
    // no son del agente en ese alcance).
    const cantidad = computed(() => (alcance.value === 'all' ? totalAlcance.value : ids.value.length));

    const puedeEnviar = computed(() => {
        if (!agenteSel.value || !resumen.value || spinner.value || loadingResumen.value) return false;
        if (accion.value === 'trasladar' && !destinoSel.value) return false;
        if (alcance.value === 'all') return totalAlcance.value > 0;
        return ids.value.length > 0 && !excedeLimite.value;
    });

    // ── Confirmacion ─────────────────────────────────────────────────────────
    // Para borrar todo se pide escribir el numero de inmuebles: es definitivo.
    const confirmacionTexto = ref('');
    const requiereTeclear = computed(() => accion.value === 'eliminar' && alcance.value === 'all');
    const confirmacionValida = computed(() =>
        !requiereTeclear.value || confirmacionTexto.value.trim() === String(totalAlcance.value)
    );

    const abrirConfirmacion = () => {
        if (!puedeEnviar.value) return;
        confirmacionTexto.value = '';
        confirmActive.value = true;
    };

    const cerrarConfirmacion = () => { confirmActive.value = false; };

    const cuerpo = () => {
        const body = {
            type: alcance.value,
            email: agenteSel.value.email,
            incluir_hijos: incluirHijos.value,
        };
        if (alcance.value === 'ids') body.ids = ids.value;
        if (accion.value === 'trasladar') body.destino_email = destinoSel.value.email;
        return body;
    };

    const ejecutar = async () => {
        if (!confirmacionValida.value) return;
        confirmActive.value = false;
        spinner.value = true;
        resultado.value = null;
        const body = cuerpo();

        try {
            if (accion.value === 'eliminar') {
                const data = await store.dispatch('appProperties/deleteMultiplePropertiesAction', body);
                resultado.value = { accion: 'eliminar', ...data };
                showAlert(`Eliminando ${data.total_en_proceso} inmueble(s). El borrado es asíncrono.`, 'success');
            } else {
                const res = await store.dispatch('appProperties/trasladarInmueblesAction', body);
                resultado.value = { accion: 'trasladar', ...(res.data || {}) };
                showAlert(res.message || 'Inmuebles trasladados', 'success');
                // El traslado es sincrono: los conteos ya cambiaron.
                cargarResumen();
            }
            entrada.value = '';
        } catch (error) {
            const status = error.response?.status;
            const payload = error.response?.data;

            if (status === 404 && body.type === 'ids') {
                // Ningun id esta en el alcance del agente: no se hizo nada.
                resultado.value = {
                    accion: accion.value,
                    type: 'ids',
                    total_recibidos: ids.value.length,
                    total_en_proceso: 0,
                    total_trasladados: 0,
                    ids_ignorados: payload?.errorMessage?.ids_ignorados || ids.value,
                };
            }
            const porDefecto = accion.value === 'eliminar'
                ? 'Error al iniciar la eliminación. No se borró nada, puedes reintentar.'
                : 'Error al trasladar. No se hizo ningún cambio, puedes reintentar.';
            showAlert(mensajeError(error, porDefecto), status === 404 ? 'warning' : 'danger');
        } finally {
            spinner.value = false;
        }
    };

    const limpiar = () => {
        entrada.value = '';
        resultado.value = null;
        destinoSel.value = null;
        store.commit('appProperties/clearUltimoResultado');
    };

    const copiarIgnorados = async () => {
        const lista = resultado.value?.ids_ignorados || [];
        if (!lista.length) return;
        try {
            await navigator.clipboard.writeText(lista.join(', '));
            showAlert('Ids ignorados copiados al portapapeles', 'success');
        } catch (e) {
            showAlert('No se pudo copiar al portapapeles', 'warning');
        }
    };

    onMounted(() => cargarAgentes());

    return {
        spinner, alertTable, resultado, confirmActive,
        agentes, agenteSel, loadingAgentes, buscarAgentes, textoSinAgentes,
        resumen, loadingResumen, incluirHijos, tieneHijos, totalAlcance,
        accion, alcance, destinoSel, destinos,
        entrada, ids, duplicados, invalidos, excedeLimite, cantidad, puedeEnviar,
        maxIds: MAX_IDS_POR_SOLICITUD,
        confirmacionTexto, requiereTeclear, confirmacionValida,
        abrirConfirmacion, cerrarConfirmacion, ejecutar, limpiar, copiarIgnorados,
    };
}
